import { SbToasterComponent } from '../components'
import type { ToastConfig, ToastType } from '../types'

interface ToasterServiceOptions {
  selector?: string
}

/**
 * Service to show toast messages
 */
class ToasterService {
  public static instance?: ToasterService

  constructor(readonly options: ToasterServiceOptions = {}) {}

  public static getSingleton() {
    if (ToasterService.instance) {
      return ToasterService.instance
    }

    throw new Error(
      'Singleton of ToasterService not defined! Use setSingleton() before!',
    )
  }

  public static setSingleton() {
    if (ToasterService.instance) {
      throw new Error(`Singleton of ToasterService already defined!`)
    }
    ToasterService.instance = new ToasterService()
    return ToasterService.instance
  }

  /**
   * Get the toaster component
   */
  protected getToastContainer() {
    if (this.options.selector) {
      const toasterCo = window.document.querySelector(this.options.selector) as
        | SbToasterComponent
        | undefined

      if (!toasterCo) {
        throw new Error(
          `Toaster component not found for selector "${this.options.selector}"!`,
        )
      }

      return toasterCo
    }

    const toasterCo = window.document.getElementsByTagName(
      SbToasterComponent.tagName,
    )[0] as SbToasterComponent | undefined

    if (!toasterCo) {
      throw new Error('Toaster component not found!')
    }

    return toasterCo
  }

  /**
   * Remove all toasts
   */
  public removeAll() {
    this.getToastContainer().removeAllToasts()
  }

  /**
   * Remove toast added with a specific id
   * @param id  ID of the added toast
   */
  public removeById(id: ToastConfig['id']) {
    this.getToastContainer().removeToastById(id)
  }

  /**
   * Show a toast message
   * @param message The message to show
   * @param type The type of the toast
   * @param scope The scope to pass to the toast
   */
  public show(
    message: string,
    type: ToastType,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.getToastContainer().addToast(message, type, scope, config)
  }

  /**
   * Show a success toast message
   * @param message The message to show
   * @param scope The scope to pass to the toast
   */
  public success(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.getToastContainer().addSuccess(message, scope, config)
  }

  /**
   * Show a info toast message
   * @param message The message to show
   * @param scope The scope to pass to the toast
   */
  public info(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.getToastContainer().addInfo(message, scope, config)
  }

  /**
   * Show a warning toast message
   * @param message The message to show
   * @param scope The scope to pass to the toast
   */
  public warning(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.getToastContainer().addWarning(message, scope, config)
  }

  /**
   * Show a danger toast message
   * @param message The message to show
   * @param scope The scope to pass to the toast
   */
  public danger(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.getToastContainer().addDanger(message, scope, config)
  }
}

export const toasterService = ToasterService.setSingleton()
